import { toApiAffiliationTypes } from '../../utils/affiliationTypes';

// The picker never asks for "every actor": with no status the backend would scan the whole
// actor index. An empty selection in the Filter means "back to the default", not "any".
export const DEFAULT_STATUSES = ['ACTIVE'];

export const effectiveStatuses = (status) =>
  status?.length ? status : DEFAULT_STATUSES;

export const sortingFieldNames = {
  ID: 'id',
  AUDIENCE: 'audience',
  ACTOR: 'name',
  PLATFORM: 'source_type',
  STATUS: 'status',
  URL: 'url',
  COUNTRY: 'country',
  RELEVANCE: 'relevance',
};

const exclusiveFiltersOf = (state) => {
  const filters = [];
  if (state.countriesExclude === true) filters.push('origin_country_ids');
  if (state.affiliationCountriesExclude === true) filters.push('country_ids');
  if (state.audienceExclude === true) filters.push('audience_lte', 'audience_gte');
  if (state.geoGroupExclude) filters.push('country_group_ids');
  if (state.botsExclude === true) filters.push('source_bots');
  if (state.IoCsExclude === true) filters.push('entity_types');
  if (state.sourceTagsExclude === true) filters.push('tags');
  if (state.affiliationTypesExclude === true) filters.push('affiliation_types');
  return filters;
};

// Query string of the picker's GET /API/v2/actors request. Group members are not excluded
// (an exclusion is a must_not over the whole index); `mark_group_id` makes the backend flag
// them with `in_group` instead. The page fetches only when this string changes.
export const buildActorsQuery = (state, page, groupId) => {
  const urlParams = new URLSearchParams();
  urlParams.set('size', '100');
  urlParams.set('page', String(page ?? 1));

  if (state.searchQuery) urlParams.set('q', state.searchQuery);
  state.platforms?.forEach((platform) => urlParams.append('source_types', platform));
  state.countries?.forEach((country) => urlParams.append('origin_country_ids', country));
  if (state.audience_lte !== null && state.audience_lte?.length > 0) {
    urlParams.set('audience_lte', state.audience_lte);
  }
  if (state.audience_gte !== null && state.audience_gte?.length > 0) {
    urlParams.set('audience_gte', state.audience_gte);
  }
  effectiveStatuses(state.status).forEach((status) => urlParams.append('statuses', status));
  state.affiliationCountries?.forEach((country) => urlParams.append('country_ids', country));
  state.IoCs?.forEach((IoC) => urlParams.append('entity_types', IoC));
  if (state.showOnlyBots) urlParams.set('is_bot', state.showOnlyBots);
  state.geoGroup?.forEach((geoGroup) => urlParams.append('country_group_ids', geoGroup));
  state.sourceTags?.forEach((tag) => urlParams.append('tags', tag));
  if (state.affiliationTypes?.length) {
    toApiAffiliationTypes(state.affiliationTypes).forEach((type) =>
      urlParams.append('affiliation_types', type),
    );
  }
  exclusiveFiltersOf(state).forEach((filter) => urlParams.append('exclusive_filters', filter));

  urlParams.set('mark_group_id', groupId);

  const sorting = state.sorting;
  if (sorting?.fieldName) {
    const prefix = sorting.isAscending ? '' : '-';
    if (Array.isArray(sorting.fieldName)) {
      sorting.fieldName.forEach((field) => urlParams.append('sorting', `${prefix}${field}`));
    } else {
      urlParams.set('sorting', `${prefix}${sortingFieldNames[sorting.fieldName]}`);
    }
  }

  return urlParams.toString();
};

// Body of POST /API/v2/actor-groups/{id}/sources/from-filter ("select all"). No member
// exclusion here either: the backend task skips actors already in the group.
export const buildFilterBody = (state) => {
  const body = {};
  if (state.searchQuery) body.q = state.searchQuery;
  if (state.platforms?.length) body.source_types = state.platforms;
  if (state.countries?.length) body.origin_country_ids = state.countries;
  if (state.audience_lte) body.audience_lte = parseInt(state.audience_lte);
  if (state.audience_gte) body.audience_gte = parseInt(state.audience_gte);
  body.statuses = effectiveStatuses(state.status);
  if (state.affiliationCountries?.length) body.country_ids = state.affiliationCountries;
  if (state.IoCs?.length) body.entity_types = state.IoCs;
  if (state.showOnlyBots) body.is_bot = true;
  if (state.geoGroup?.length) body.country_group_ids = state.geoGroup;
  if (state.sourceTags?.length) body.tags = state.sourceTags;
  if (state.affiliationTypes?.length) body.affiliation_types = toApiAffiliationTypes(state.affiliationTypes);
  body.exclusive_filters = exclusiveFiltersOf(state);
  return body;
};

// Rows the user can still add: members of the group are shown, but not selectable.
export const isGroupMember = (source) => source?.in_group === true;
export const selectableSources = (sources) => (sources ?? []).filter((source) => !isGroupMember(source));
